import { authHeaders } from "./acting.js";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  batchPourMl,
  flavourColour,
  grossProfit,
  latestBuild,
  NEUTRAL_COLOUR,
  specTotals,
  toFlavourFamily,
  type ChatBuild,
  type ChatMessage,
  type ChatThread,
  type ChatWheel,
  DEFAULT_SHELF,
  SHELF_CHOICES,
  type ShelfScope,
  type Spec,
  type SpecOverride,
} from "@baros/core";
import { renewOrEnd, type Library } from "./api.js";
import { money, pct } from "./format.js";
import { ChatWheelCard } from "./wheel/ChatWheelCard.js";

/** A build the chat proposed, handed to the editor. */
export type ProposedBuild = ChatBuild;

/** A message as the screen shows it. */
interface Shown {
  readonly role: "user" | "assistant";
  readonly content: string;
  /** Who asked: "You", or their name. Empty for the chat's answers. */
  readonly who: string;
  /** Flavour wheels the answer drew, under its text. */
  readonly wheels?: readonly ChatWheel[] | undefined;
}

interface Props {
  readonly token: string;
  readonly venueId: string;
  readonly userId: string;
  readonly library: Library;
  readonly overrides: ReadonlyMap<string, SpecOverride>;
  readonly canChat: boolean;
  readonly canEdit: boolean;
  /** The thread being continued, or null for a new one. */
  readonly thread: ChatThread | null;
  /** What the thread holds so far, oldest first. */
  readonly history: readonly ChatMessage[];
  readonly subject: Spec | undefined;
  /** Text to open the composer with — the flavour wheel hands its tray over this way. */
  readonly initialInput?: string | undefined;
  /** Rendered at the start of the header: the thread list's toggle on a narrow screen. */
  readonly headerStart?: ReactNode;
  readonly onPickSubject: (specId: string | undefined) => void;
  readonly onApply: (build: ProposedBuild) => Promise<void>;
  /** The thread was started, or changed by a turn. */
  readonly onThread: (thread: ChatThread, created: boolean) => void;
  /** The chat added an ingredient; the library should be re-read. */
  readonly onLibraryChanged?: (() => void) | undefined;
}

const CHAT_URL = (import.meta.env.VITE_CHAT_URL ?? "").replace(/\/$/, "") || "/";

function shown(m: ChatMessage, userId: string): Shown {
  return {
    role: m.role,
    content: m.content,
    who: m.role === "assistant" ? "" : m.by === userId ? "You" : m.byName,
    wheels: m.wheels,
  };
}

export function Chat(p: Props) {
  const [messages, setMessages] = useState<Shown[]>(() => p.history.map((m) => shown(m, p.userId)));
  const [input, setInput] = useState(p.initialInput ?? "");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // A thread reopened shows the build it last proposed, as it did then.
  const [draft, setDraft] = useState<ProposedBuild | null>(() => latestBuild(p.history));
  const [picking, setPicking] = useState(false);
  // Narrow only: the working spec is a sheet over the conversation rather
  // than a column beside it. Above 900px `mobile.css` hides the bar and the
  // scrim and the panel is simply the right-hand column, so this is inert.
  const [specOpen, setSpecOpen] = useState(false);
  /**
   * Which shelf the chat may reach for (#165). Kept for the conversation
   * rather than stored: it is how somebody is thinking right now, not a
   * property of the thread, and the next person to open it is as likely to
   * be asking the other question.
   */
  const [shelf, setShelf] = useState<ShelfScope>(DEFAULT_SHELF);
  const [shelfOpen, setShelfOpen] = useState(false);
  const [spend, setSpend] = useState<number>(0);
  /** The thread this conversation is writing to, once there is one. */
  const chatId = useRef<string | null>(p.thread?.chatId ?? null);
  const bottom = useRef<HTMLDivElement>(null);

  const ingredients = useMemo(
    () => new Map(p.library.ingredients.map((i) => [i.ingredientId, i])),
    [p.library.ingredients],
  );
  const specs = useMemo(() => new Map(p.library.specs.map((s) => [s.specId, s])), [p.library.specs]);
  const price = p.subject ? p.overrides.get(p.subject.specId)?.price ?? 24 : 22;

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: "end" });
  }, [messages, busy]);

  const shownLines = draft
    ? draft.lines.map((l) => ({ ingredientId: l.ingredientId, ml: l.ml, unit: l.unit ?? ("ml" as const), qty: l.qty, batched: l.batched }))
    : (p.subject?.lines ?? []);
  const totals = specTotals({ lines: shownLines, sundries: p.subject?.sundries ?? 0.1 }, ingredients);
  const gp = grossProfit(price, totals.cost);

  async function ask() {
    const text = input.trim();
    if (!text || busy) return;
    const next: Shown[] = [...messages, { role: "user", content: text, who: "You" }];
    setMessages(next);
    setInput("");
    setBusy(true);
    setError(null);
    setNotice(null);
    let assistant = "";
    let wheels: ChatWheel[] = [];
    const push = () =>
      setMessages([...next, { role: "assistant", content: assistant || "…", who: "", wheels }]);
    push();

    try {
      const ask = (token: string) =>
        fetch(CHAT_URL, {
          method: "POST",
          headers: { ...authHeaders(token), "content-type": "application/json" },
          // Only the question: the thread's history is the server's to read,
          // so everybody continuing it sends the model the same conversation.
          body: JSON.stringify({
            venueId: p.venueId,
            ...(chatId.current ? { chatId: chatId.current } : { specId: p.subject?.specId }),
            price,
            message: text,
            shelf,
          }),
        });
      let res = await ask(p.token);
      // The chat is the screen most likely to be left open past the hour, so
      // it gets the same answer to a dead token as every other call: refresh
      // and ask again, and sign out when that will not do it. A refused turn
      // never reached the model, so asking again costs nothing.
      if (res.status === 401) {
        const renewed = await renewOrEnd(p.token);
        if (renewed) res = await ask(renewed);
      }
      if (!res.ok || !res.body) {
        throw new Error((await res.text().catch(() => "")) || `${res.status} ${res.statusText}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let nl: number;
        while ((nl = buffer.indexOf("\n")) >= 0) {
          const line = buffer.slice(0, nl).trim();
          buffer = buffer.slice(nl + 1);
          if (!line) continue;
          let event: Record<string, unknown>;
          try {
            event = JSON.parse(line) as Record<string, unknown>;
          } catch {
            continue;
          }
          switch (event["type"]) {
            case "chat": {
              const thread = event["chat"] as ChatThread;
              chatId.current = thread.chatId;
              p.onThread(thread, event["created"] === true);
              break;
            }
            case "text":
              assistant += String(event["text"] ?? "");
              push();
              break;
            case "ingredient": {
              const ing = event["ingredient"] as { name?: string; costPerMl?: number; abv?: number } | undefined;
              setNotice(
                `Added ${ing?.name ?? "an ingredient"} to the list at $${(ing?.costPerMl ?? 0).toFixed(3)}/mL, ${ing?.abv ?? 0}% abv — an estimate, marked unconfirmed until a manager checks it.`,
              );
              p.onLibraryChanged?.();
              break;
            }
            case "build":
              setDraft(event["build"] as ProposedBuild);
              break;
            case "wheel":
              wheels = [...wheels, event["wheel"] as ChatWheel];
              push();
              break;
            case "notice":
              setNotice(String(event["message"]));
              break;
            case "error":
              setError(String(event["message"]));
              break;
            case "done":
              setSpend((s) => s + Number(event["costCents"] ?? 0));
              break;
          }
        }
      }
      if (!assistant.trim() && wheels.length === 0) {
        setMessages(next);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "The chat failed");
      setMessages(next);
    } finally {
      setBusy(false);
    }
  }

  // The serve as the proposal has it, or as the spec on file has it. A field
  // the chat left blank is left out rather than shown as a dash.
  const serve = draft ?? p.subject;
  const structure: (readonly [string, string])[] = [
    ["Method", serve?.method ?? ""],
    ["Glass", serve?.glass ?? ""],
    ["Ice", serve?.ice ?? ""],
    ["Garnish", serve?.garnish ?? ""],
  ].flatMap(([label, value]) => (value!.trim() ? [[label!, value!.trim()] as const] : []));

  // A batched build reads as the bar makes it: what is mixed ahead, with what
  // the batch pours, then what is done to order. Unbatched, it is one list.
  const inBatch = shownLines.filter((l) => l.batched);
  const batchMl = batchPourMl(shownLines, ingredients);
  const batchName = draft?.batchName ?? p.subject?.batch ?? "";
  const grouped: (typeof shownLines[number] | { readonly heading: string })[] =
    inBatch.length === 0
      ? [...shownLines]
      : [
          { heading: `${batchName.trim() || "In the batch"} · pours ${+batchMl.toFixed(1)} mL a serve` },
          ...inBatch,
          { heading: "To order" },
          ...shownLines.filter((l) => !l.batched),
        ];

  const subjectLabel = p.subject ? `Revising ${p.subject.name} · v${p.subject.version}` : "New item · nothing saved yet";
  const candidates = [...p.library.specs].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="split chat-split">
      <section className="pane chat-pane">
        <div style={{ padding: "14px 20px 10px", borderBottom: "1px solid var(--ln)", display: "flex", flexDirection: "column", gap: 10 }}>
          <div className="row" style={{ alignItems: "baseline", gap: 12 }}>
            {p.headerStart}
            <div className="label" style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {subjectLabel}
            </div>
            <span className="spacer" />
            <div className="label pos chat-live" style={{ letterSpacing: "0.12em" }}>Live cost feed</div>
          </div>
          <div className="row chat-subject">
            <span className="label">Working on</span>
            <button className="btn" style={{ borderColor: "var(--tx)", background: "var(--rz)", fontFamily: "var(--sans)", fontSize: 13, textTransform: "none", letterSpacing: 0 }} onClick={() => setPicking((v) => !v)}>
              <span className="chat-subject-name">{p.subject?.name ?? "A new item"}</span>
              <span className="muted">▾</span>
            </button>
            {spend > 0 && <span className="label">This session: ${(spend / 100).toFixed(3)}</span>}
          </div>
          {picking && (
            <div className="scroll keep-scroll" style={{ maxHeight: 220, border: "1px solid var(--ln)", borderRadius: 2, background: "var(--sf)" }}>
              <div className="label" style={{ padding: "10px 12px 4px" }}>
                {p.thread ? "Starts a new chat about the drink you pick" : "What this chat is about"}
              </div>
              <button className="trow" style={{ minHeight: 44, padding: "0 12px" }} onClick={() => { p.onPickSubject(undefined); setPicking(false); }}>
                <span style={{ flex: 1 }}>A new item</span>
              </button>
              {candidates.map((s) => (
                <button key={s.specId} className="trow" style={{ minHeight: 44, padding: "0 12px" }} onClick={() => { p.onPickSubject(s.specId); setPicking(false); }}>
                  <span style={{ flex: 1 }}>{s.name}</span>
                  <span className="label">{s.status}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="scroll" style={{ padding: 20, display: "flex", flexDirection: "column", gap: 18 }}>
          {messages.length === 0 && (
            <div className="muted" style={{ fontSize: 14, lineHeight: 1.6, maxWidth: 560 }}>
              {p.subject
                ? `Ask what to change in ${p.subject.name}: lift the GP, lower the ABV, swap a line for something held. Every number comes from the costing engine, and a proposed build lands in the draft on the right.`
                : "Describe the drink you are after — a base, a direction, a price point — and the development chat will build it from the ingredient list and cost it live."}
              <div style={{ marginTop: 10, fontSize: 13 }}>
                The chat is kept, and everyone on the team can read and add to it.
              </div>
              {!p.canChat && <div className="msg err" style={{ marginTop: 12 }}>Your role does not include the development chat.</div>}
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className="bubble-wrap" style={{ alignSelf: m.role === "user" ? "flex-end" : "flex-start", maxWidth: m.role === "user" ? "72%" : "86%" }}>
              <div className="label">{m.role === "user" ? m.who : "Development · grounded in the library"}</div>
              <div className={`bubble ${m.role === "user" ? "user" : "ai"}`}>{m.content}</div>
              {m.wheels?.map((wheel, w) => (
                <ChatWheelCard
                  key={w}
                  wheel={wheel}
                  token={p.token}
                  ingredients={ingredients}
                  specs={specs}
                  onAnswer={p.canChat && !busy ? setInput : undefined}
                />
              ))}
            </div>
          ))}
          {notice && <div className="msg" style={{ borderColor: "var(--warn)" }}>{notice}</div>}
          {error && <div className="msg err">{error}</div>}
          <div ref={bottom} />
        </div>

        <button
          type="button"
          className="chat-spec-bar"
          aria-expanded={specOpen}
          aria-controls="chat-working-spec"
          onClick={() => setSpecOpen((v) => !v)}
        >
          <span className="chat-spec-bar-name">{draft?.name ?? p.subject?.name ?? "Nothing yet"}</span>
          <span className="spacer" />
          <span className="num chat-spec-bar-cost">{money(totals.cost)}</span>
          <span className="label">GP {pct(gp)}</span>
          <span className="chat-spec-bar-caret" aria-hidden="true">{specOpen ? "▾" : "▴"}</span>
        </button>

        <form
          className="row chat-composer"
          style={{ borderTop: "1px solid var(--ln)", padding: "14px 20px", gap: 12 }}
          onSubmit={(e) => { e.preventDefault(); void ask(); }}
        >
          <div className="chat-plus">
            <button
              type="button"
              className="btn chat-plus-btn"
              aria-expanded={shelfOpen}
              aria-haspopup="menu"
              aria-label="What the chat may use"
              title="What the chat may use"
              onClick={() => setShelfOpen((v) => !v)}
            >
              +
              {/* The default needs no label; anything else does, or you
                  cannot tell you left it on "External only" last night. */}
              {shelf !== DEFAULT_SHELF && (
                <span className="chat-plus-current">
                  {SHELF_CHOICES.find((c) => c.scope === shelf)?.label}
                </span>
              )}
            </button>
            {shelfOpen && (
              <>
                <button
                  type="button"
                  className="chat-plus-scrim"
                  aria-label="Close"
                  onClick={() => setShelfOpen(false)}
                />
                <div className="chat-plus-menu" role="menu">
                  <div className="label chat-plus-head">What the chat may use</div>
                  {SHELF_CHOICES.map((c) => (
                    <button
                      key={c.scope}
                      type="button"
                      role="menuitemradio"
                      aria-checked={shelf === c.scope}
                      className="chat-plus-item"
                      onClick={() => {
                        setShelf(c.scope);
                        setShelfOpen(false);
                      }}
                    >
                      <span className="chat-plus-tick" aria-hidden="true">{shelf === c.scope ? "✓" : ""}</span>
                      <span>
                        <span className="chat-plus-label">{c.label}</span>
                        <span className="chat-plus-hint">{c.hint}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          <input
            type="text"
            style={{ flex: 1, height: 56, background: "var(--sf)" }}
            placeholder={p.subject ? `What would you change in ${p.subject.name}?` : "Something long, low abv, Montenegro and mûre…"}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={busy || !p.canChat}
          />
          <button className="btn primary" type="submit" disabled={busy || !p.canChat || !input.trim()}>
            {busy ? "Thinking…" : "Ask"}
          </button>
        </form>
      </section>

      {specOpen && (
        <button
          type="button"
          className="chat-spec-scrim"
          aria-label="Close the working recipe"
          onClick={() => setSpecOpen(false)}
        />
      )}
      <aside
        id="chat-working-spec"
        className={`aside chat-aside${specOpen ? " open" : ""}`}
      >
        <div style={{ padding: 16, borderBottom: "1px solid var(--ln)" }}>
          <div className="row">
            <div className="label">{draft ? "Proposed build" : p.subject ? "Current recipe" : "Working recipe"}</div>
            <span className="spacer" />
            {/* Raised as a sheet on a narrow screen, where the summary bar
                that opened it is underneath. Hidden by mobile.css once the
                panel is the right-hand column again. */}
            <button type="button" className="btn sm chat-spec-close" onClick={() => setSpecOpen(false)}>
              Close
            </button>
          </div>
          <div className="serif" style={{ fontSize: 26, marginTop: 6, lineHeight: 1.15 }}>{draft?.name ?? p.subject?.name ?? "Nothing yet"}</div>
          {draft?.note && <div className="muted" style={{ fontSize: 12, marginTop: 6, lineHeight: 1.45 }}>{draft.note}</div>}
        </div>
        <div className="scroll" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
          {grouped.map((l, i) => {
            if ("heading" in l) {
              return (
                <div key={i} className="label chat-group">
                  {l.heading}
                </div>
              );
            }
            const ing = ingredients.get(l.ingredientId);
            const colour = !ing || ing.kind === "dilution" || !toFlavourFamily(ing.aromaFamily) ? NEUTRAL_COLOUR : flavourColour(ing.aromaFamily);
            return (
              <div key={i} className="row" style={{ borderBottom: "1px solid var(--ln)", paddingBottom: 8 }}>
                <span className="swatch" style={{ background: colour, height: 26 }} />
                <span style={{ flex: 1, fontSize: 13 }}>{ing?.name ?? l.ingredientId}</span>
                <span className="num muted" style={{ fontSize: 12 }}>
                  {l.unit === "dash" || l.unit === "drop" ? `${l.qty ?? 1} ${l.unit}` : l.unit === "top" ? "top" : `${+l.ml.toFixed(1)} mL`}
                </span>
                <span className="num" style={{ fontSize: 12, width: 52, textAlign: "right" }}>{money(l.ml * (ing?.costPerMl ?? 0))}</span>
              </div>
            );
          })}
          {shownLines.length === 0 && <div className="label">No build yet</div>}
          {/* How it is made, under what goes in it: the rest of the recipe card. */}
          {structure.length > 0 && (
            <div className="chat-structure">
              <div className="label">Recipe</div>
              {structure.map(([k, v]) => (
                <div key={k} className="kv">
                  <span className="k">{k}</span>
                  <span className="v">{v}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ borderTop: "1px solid var(--ln)", padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
          <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
            <span className="label">Cost / serve</span>
            <span className="num" style={{ fontSize: 30 }}>{money(totals.cost)}</span>
          </div>
          <div className="row num muted" style={{ justifyContent: "space-between", fontSize: 11 }}>
            <span>At {money(price)} · GP {pct(gp)}</span>
            <span>ABV {pct(totals.abv)} · {totals.standardDrinks.toFixed(1)} std</span>
          </div>
          <button className="btn primary" disabled={!draft || !p.canEdit} onClick={() => draft && void p.onApply(draft)}>
            {p.subject
              ? draft?.variant
                ? `Save as the ${draft.variant} recipe of ${p.subject.name.split(" · ")[0]}`
                : `Open ${draft ? "proposal" : "recipe"} in the editor`
              : "Save as a new item"}
          </button>
        </div>
      </aside>
    </div>
  );
}
